<template>
    <BasicButton
        v-if="isExternalLink"
        v-bind="basicButtonProps"
        :uppercase="computedUppercase"
        :size="computedSize"
        :target="computedTarget"
        :class="attrs.class"
    >
        <slot></slot>
    </BasicButton>
    <NuxtLink
        v-else
        v-slot="{ navigate }"
        v-bind="nuxtLinkProps"
        :target="computedTarget"
        custom
    >
        <BasicButton
            v-bind="basicButtonProps"
            :uppercase="computedUppercase"
            :size="computedSize"
            :target="computedTarget"
            :class="attrs.class"
            @click="navigate"
        >
            <slot></slot>
        </BasicButton>
    </NuxtLink>
</template>

<script setup lang="ts">
import BasicButton, {
    type BasicButtonProps,
} from '@atoms/BasicButton/BasicButton.vue';
import { NuxtLink } from '#components';
import type { NuxtLinkProps } from 'nuxt/app';

export type BasicNuxtLinkProps = Partial<BasicButtonProps> &
    Partial<NuxtLinkProps>;

const props = withDefaults(defineProps<BasicNuxtLinkProps>(), {
    color: 'info',
    isLink: true,
    size: undefined,
    isInsideText: undefined,
});

const attrs = useAttrs();

const filterProps = <T extends {}>(propsDefinitions: T) => {
    const propKeys = Object.keys(propsDefinitions);
    const filtered: Record<string, unknown> = {};
    propKeys.forEach((key) => {
        const propKey = key as keyof typeof props;
        if (propKey && props[propKey]) filtered[propKey] = props[propKey];
    });
    return filtered as T;
};

const nuxtLinkProps = computed(() =>
    filterProps<NuxtLinkProps>(NuxtLink.props)
);

const basicButtonProps = computed(() =>
    filterProps<BasicButtonProps>(BasicButton.props)
);

// TODO: changing to useLink could be a potential improvement
// const { navigate, href } = useLink({ to: props.to || '' });

const computedSize = computed(() => {
    return props.isInsideText ? 'm' : props.size;
});

const computedUppercase = computed(() => {
    if (props.uppercase) {
        return props.uppercase;
    }
    return !props.isInsideText;
});

const computedTarget = computed(() => {
    if (props.target) return props.target;
    return unref(isExternalLink) ? '_blank' : '_self';
});

const isExternalLink = computed(() => {
    const isToExternal =
        typeof props.to === 'string' &&
        (props.to.startsWith('http') || props.to.startsWith('mailto:'));
    const isHrefExternal = !!(
        props.href?.startsWith('http') || props.href?.startsWith('mailto:')
    );
    return isToExternal || isHrefExternal;
});
</script>
